/**
 * Authentication Provider with modern React patterns
 */

import React, { useEffect, useState, useCallback } from 'react'
import AuthContext from './AuthContext'
import { authService } from '../../services/authService'
import type {
  AuthContextType,
  FeatureFlags,
  User,
  UserProfile,
  UserPermissions,
} from '../../types/auth'

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [permissions, setPermissions] = useState<UserPermissions | null>(null)
  const [features, setFeatures] = useState<FeatureFlags | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = !!user && authService.isAuthenticated()

  const initializeAuth = useCallback(async () => {
    try {
      if (authService.isAuthenticated()) {
        // Try to get data from cache first (respects 1-hour TTL)
        try {
          const userData = await authService.getSessionData()
          setUser(userData.user)
          setProfile(userData.profile)
          setPermissions(userData.permissions)
          setFeatures(userData.features)
        } catch {
          /* Silent fail */
          // If API call fails, try stored data as fallback
          const storedData = authService.getStoredUserData()
          if (storedData.user) {
            setUser(storedData.user)
            setProfile(storedData.profile)
            setPermissions(storedData.permissions)
            setFeatures(storedData.features)
          } else {
            // No data available, logout
            await logout()
          }
        }
      } else {
        // Clear any stale data
        authService.clearAuthData()
      }
    } catch {
      /* Silent fail */
      authService.clearAuthData()
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Initialize auth state on app load
  useEffect(() => {
    initializeAuth()
  }, [initializeAuth])

  // Listen for features updates from token refresh
  useEffect(() => {
    const handleFeaturesUpdate = (event: CustomEvent<FeatureFlags>) => {
      setFeatures(event.detail)
    }

    window.addEventListener(
      'featuresUpdated',
      handleFeaturesUpdate as EventListener
    )

    return () => {
      window.removeEventListener(
        'featuresUpdated',
        handleFeaturesUpdate as EventListener
      )
    }
  }, [])

  const login = async (email: string, password: string): Promise<void> => {
    const response = await authService.login(email, password)

    // Update auth state
    setUser(response.user)
    setProfile(response.profile)
    setPermissions(response.permissions)
    setFeatures(response.features ?? null)

    // Note: AgentsProvider will automatically load agents when isAuthenticated changes
    // No need to pre-warm cache here (would cause duplicate API calls)
  }

  const logout = async (): Promise<void> => {
    try {
      await authService.logout()
    } catch {
      /* Silent fail */
    } finally {
      // Always clear state
      setUser(null)
      setProfile(null)
      setPermissions(null)
      setFeatures(null)
    }
  }

  const refreshUserData = async (): Promise<void> => {
    if (!isAuthenticated) return

    try {
      const userData = await authService.getSessionData()
      setUser(userData.user)
      setProfile(userData.profile)
      setPermissions(userData.permissions)
      setFeatures(userData.features)
    } catch {
      /* Silent fail */
      // If refresh fails, logout user
      await logout()
    }
  }

  const contextValue: AuthContextType = {
    user,
    profile,
    permissions,
    features,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshUserData,
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}
